import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  ModalClose,
  ModalDialog,
  Radio,
  RadioGroup,
  Sheet,
  Switch,
  Typography,
} from "@mui/joy";
import { FormEvent, useState } from "react";

import { useCreateRouteLabelGroupMutation, useCreateRouteLabelMutation } from "@/hooks/routeLabels";

import { useModal } from "./useModal";

type LabelKind = "flag" | "options";

const KindRadio = ({
  value,
  title,
  description,
}: {
  value: LabelKind;
  title: string;
  description: string;
}) => (
  <Radio
    value={value}
    sx={{ mb: 1 }}
    label={
      <div>
        <Typography component="span">{title}</Typography>
        <Typography level="body-sm" sx={{ display: "block", color: "neutral.600", mt: 0.25 }}>
          {description}
        </Typography>
      </div>
    }
  />
);

const AddRouteLabel = ({
  onCreated,
}: {
  // Gets the new label's uuid, and whether it has options.
  onCreated?: (uuid: string, hasOptions: boolean) => void;
}) => {
  const { close } = useModal();
  const createGroup = useCreateRouteLabelGroupMutation();
  const createLabel = useCreateRouteLabelMutation();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<LabelKind>("options");
  // Most labels are meant for climbers to see, so start with it on.
  const [isPublic, setIsPublic] = useState(true);

  const trimmedName = name.trim();
  const isPending = createGroup.isPending || createLabel.isPending;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedName) {
      return;
    }
    try {
      // A label with options is a group on the server, and one without is a root label.
      const created =
        kind === "options"
          ? await createGroup.mutateAsync({ name: trimmedName, isPublic })
          : await createLabel.mutateAsync({ name: trimmedName, isPublic });
      onCreated?.(created.uuid, kind === "options");
      close();
    } catch {
      // The toast has the reason, and the dialog stays open to fix it.
    }
  };

  return (
    <ModalDialog sx={{ width: 440, maxWidth: "calc(100vw - 32px)" }}>
      <ModalClose />
      <DialogTitle>Add label</DialogTitle>
      <DialogContent sx={{ textAlign: "left", overflowX: "hidden" }}>
        <form onSubmit={onSubmit}>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Name</FormLabel>
            <Input
              // oxlint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              slotProps={{ input: { maxLength: 255 } }}
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Type</FormLabel>
            <RadioGroup value={kind} onChange={(event) => setKind(event.target.value as LabelKind)}>
              <KindRadio
                value="flag"
                title="Label only"
                description="e.g. “Showcase”, “Insta friendly”."
              />
              <KindRadio
                value="options"
                title="Label with options"
                description="Has a set of choices, like “Time spent” with “15 mins”, “30 mins” and “1 hour”."
              />
            </RadioGroup>
          </FormControl>

          <Sheet variant="soft" sx={{ p: 1.5, borderRadius: "sm", mb: 2 }}>
            <FormControl orientation="horizontal" sx={{ justifyContent: "space-between", gap: 2 }}>
              <div>
                <FormLabel>Visible to climbers</FormLabel>
                <FormHelperText>
                  {kind === "options"
                    ? "Applies to all of its options."
                    : "Climbers can see this label on routes."}
                </FormHelperText>
              </div>
              <Switch checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} />
            </FormControl>
          </Sheet>
          <DialogActions>
            <Button type="submit" disabled={!trimmedName} loading={isPending}>
              Add label
            </Button>
            <Button variant="plain" color="neutral" onClick={close}>
              Cancel
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </ModalDialog>
  );
};

export default AddRouteLabel;
